import { useEffect, useState } from "react";
import { t } from "i18next";
import { cn } from "@edusogno/lib/utils";
import { ucFirst } from "@/utils/string";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useCheckBookLiveCorrectionMutation } from "@/data/exercises";
import { useMe, useSetNotificationMetadataAsSeen } from "@/data/user";
import {
  MIXPANEL_EVENT_TYPES,
  maixpanelTrackEventProps,
} from "@/mixpanel/MixpanelUtils";
import {
  Exercise,
  LoginUserItem,
  NotificationType,
  UserNotificationItem,
  UserNotificationPaginator,
} from "@/types";
import dayjs from "dayjs";
import routes from "@/config/routes.ts";
import Button from "@edusogno/primitives/button";
import EyeIcon from "../icons/EyeIcon";
import CardItem from "@/components/ui/CardItem.tsx";
import ArrowRight from "../icons/ArrowRight";
import useGlobals from "@/utils/hooks/useGlobals";
import EventBadge from "@edusogno/composites/event-badge";
import API_ENDPOINTS from "@/data/client/endpoints";
import useCorrection from "@/utils/hooks/useCorrection.ts";
import NewNotificationIcon from "../icons/NewNotificationIcon";

interface ExerciseItemProps {
  status: "todo" | "done";
  exercise: Exercise;
  canBookWritings: boolean;
}

const ExerciseItem = (props: ExerciseItemProps) => {
  const navigate = useNavigate();
  const { getCorrection } = useCorrection();
  const { isPot, setGlobalState } = useGlobals();
  const { mutate: mutateCheckBookLiveCorrection } =
    useCheckBookLiveCorrectionMutation();

    function getIELTSListeningBandScore(userScore: string | null | undefined): string {
      if (!userScore) return "N/A";
    
      const [rawScore] = userScore.split("/");
      const score = parseInt(rawScore.trim(), 10);
    
      if (isNaN(score)) return "N/A";
    
      if (score >= 39) return "9";
      if (score >= 37) return "8.5";
      if (score >= 35) return "8";
      if (score >= 32) return "7.5";
      if (score >= 30) return "7";
      if (score >= 26) return "6.5";
      if (score >= 23) return "6";
      if (score >= 18) return "5.5";
      if (score >= 16) return "5";
      if (score >= 13) return "4.5";
      if (score >= 11) return "4";
      if (score >= 9) return "3.5";
      if (score >= 7) return "3";
      if (score >= 5) return "2.5";
      if (score >= 3) return "2";
      return "1.5";
    }
    
    function getIELTSReadingBandScore(userScore: string | null | undefined): string {
      if (!userScore) return "N/A";
    
      const [rawScore] = userScore.split("/");
      const score = parseInt(rawScore.trim(), 10);
    
      if (isNaN(score)) return "N/A";
    
      if (score >= 39) return "9";
      if (score >= 37) return "8.5";
      if (score >= 35) return "8";
      if (score >= 33) return "7.5";
      if (score >= 30) return "7";
      if (score >= 27) return "6.5";
      if (score >= 23) return "6";
      if (score >= 19) return "5.5";
      if (score >= 15) return "5";
      if (score >= 13) return "4.5";
      if (score >= 10) return "4";
      if (score >= 8) return "3.5";
      if (score >= 6) return "3";
      if (score >= 4) return "2.5";
      if (score >= 2) return "2";
      return "1.5";
    }
    

  const {
    status,
    exercise: {
      id,
      modality,
      isNew,
      type,
      hasDeadLine: hasDeadLine,
      is_precourse: isPrecourse,
    },
    canBookWritings,
  } = props;


  const exerciseHasDeadline = hasDeadLine || props.exercise?.has_deadline;

  const [showState, setShowState] = useState(false);

  const isTodo = status === "todo";
  const isWriting = !isPot && type?.toLowerCase() === "writing";
  const isSpeaking = !isPot && modality?.toLowerCase() === "speaking";
  const votePot = props.exercise.pivot?.vote || props.exercise?.isPot;
  const exerciseLabel =
    type?.toLowerCase() === "ielts" || type?.toLowerCase() === "linguaskill"
      ? modality?.toLowerCase()
      : type?.toLowerCase();

  let isMockTest = false;
  let name: string | null = null;
  if ("name" in props.exercise) {
    isMockTest = props.exercise.name?.toLowerCase().includes("mock test");
    name = props.exercise.name;
  }
  let userScore: string | null = null;

  if ("user_score" in props.exercise) {
    userScore = props.exercise.user_score;
  }

  let exId: number | null = null;
  if ("id" in props.exercise) {
    exId = props.exercise.id;
  }

  let testName: string | null = null;
  if ("test_name" in props.exercise) {
    testName = props.exercise.test_name;
  }

  let tokenEx: string | null = null;
  if ("test_name" in props.exercise) {
    tokenEx = props.exercise.token_ex;
  }

  let corrected: boolean | null = false;
  if ("corrected" in props.exercise) {
    corrected = props.exercise.corrected;
  }

  let token: string | null = null;
  if ("token" in props.exercise) {
    token = props.exercise.token;
  }

  let toDoBy: string | null = null;
  if ("toDoBy" in props.exercise) {
    toDoBy = props.exercise.toDoBy;
  }

  const queryClient = useQueryClient();
  const { mutateAsync } = useSetNotificationMetadataAsSeen();
  // Helper function to parse category safely
  const parseCategory = (category?: string): string[] => {
    try {
      const parsed: unknown = JSON.parse(category || "[]");
      if (
        Array.isArray(parsed) &&
        parsed.every((item): item is string => typeof item === "string")
      ) {
        return parsed;
      }
      return [];
    } catch (error) {
      console.error("Failed to parse category JSON:", error);
      return [];
    }
  };

  const onDoItClick = async () => {
    const queryResults = queryClient.getQueryData<UserNotificationPaginator>([
      API_ENDPOINTS.NOTIFICATIONS,
    ]);

    const notifications = (queryResults?.items as UserNotificationItem[]) || [];
    const notification = notifications.find(
      (n) =>
        n.action === NotificationType.ADD_EXERCISES &&
        n.metadata?.some((meta) => meta.id === id && !meta.seen),
    );

    if (notification) {
      const metadata = notification.metadata.find((meta) => meta.id === id);

      if (metadata) {
        await mutateAsync({
          id_notification: notification.id,
          id_metadata: metadata.id,
        });

        queryClient.setQueryData<UserNotificationItem[]>(
          [API_ENDPOINTS.NOTIFICATIONS_SET_SEEN],
          (old) =>
            old?.map((n) =>
              n.id === notification.id
                ? {
                    ...n,
                    metadata: n.metadata.map((meta) =>
                      meta.id === metadata.id ? { ...meta, seen: true } : meta,
                    ),
                  }
                : n,
            ),
        );
      }
    }

    if (isTodo) {
      if (isMockTest) {
        navigate(routes.exercises_mock_test, {
          state: { exerciseCorrection: token as string },
        });
        return;
      }

      if (props.exercise.isPot) {
        setGlobalState("isPot", props.exercise.isPot);
        navigate(`${routes.exercises}/pot/${exId}?isPot=true`);
        return;
      }

      setGlobalState("isPot", false);

      if (modality?.toLowerCase() == "linguaskill") {
        const category = parseCategory(props.exercise.category);
      
        if (category.length > 0) {
          navigate(`${routes.exercises}/${category[0].toLowerCase()}/${token}`);
        }
      } else {
        navigate(`${routes.exercises}/${modality?.toLowerCase()}/${token}`);
      }
    } else if (!isTodo && !isMockTest) {
      if (modality?.toLowerCase() == "linguaskill") {
        const category = parseCategory(props.exercise.category);

        if (category) {
          navigate(
            `${routes.exercises}/correction/${
              category[0]?.toLowerCase() || ""
            }/${tokenEx}`,
          );
        }
      } else {
        navigate(
          `${routes.exercises}/correction/${
            type?.toLowerCase() || ""
          }/${tokenEx}`,
        );
      }
    }
  };

  const handleDoItClick = () => {
    onDoItClick()
      .then(() => {})
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    if (!showState) return;

    const timeout = setTimeout(() => {
      setShowState(false);
    }, 3000);

    return () => clearTimeout(timeout); // Fix clearing timeout correctly
  }, [showState]);

  // mixpanel integration
  const { me } = useMe();
  const mixpanelPropsData = JSON.stringify(
    maixpanelTrackEventProps("Exercise", me?.item?.user as LoginUserItem),
  );

  const ex: { type?: string; typeV2?: string } = props.exercise;

  const isBook98WL = ex?.type === "Writing" && ex?.typeV2 === "Linguaskill";

  const timer = props.exercise?.timer || props.exercise?.duration;

  return (
    <CardItem>
      <div className="flex flex-row items-center w-full gap-3 lg:gap-4">
        <div className="relative">
          {isNew && (
            <NewNotificationIcon className="absolute w-[6px] h-[6px] -top-[4px] -left-[6px]" />
          )}
          <div className="hidden md:block">
            <EventBadge
              type={ucFirst(exerciseLabel)}
              className={`!w-[116px] md:!w-[160px]  px-2 ${
                type?.toLowerCase() === "comprehension"
                  ? "!bg-[#392287] !text-[#fff]"
                  : ""
              }`}
            />
          </div>
          <div className="absolute -top-[32px] left-3 md:hidden">
            <EventBadge
              type={ucFirst(exerciseLabel)}
              className={` px-2 ${
                type?.toLowerCase() === "comprehension"
                  ? "!bg-[#392287] !text-[#fff] !w-[116px] md:!w-[160px]"
                  : ""
              }`}
            />
          </div>
        </div>

        <div className="flex flex-col w-full h-full">
          {status === "todo" && (
            <div className="flex flex-row items-center w-full gap-2">
              {isPrecourse ? (
                <p className={cn("text-xs text-primary")}>
                  {t("exercises.precourse")}
                </p>
              ) : exerciseHasDeadline === 1 ? (
                <>
                  <div className="hidden md:block">
                    <div
                      className={cn(
                        "text-xs flex flex-wrap w-full text-primary",
                        {
                          "text-[#E31837]":
                            dayjs(toDoBy as string).diff(dayjs(), "day") < 0,
                        },
                      )}
                    >
                      {type?.toLowerCase() == "comprehension" ? (
                        <></>
                      ) : (
                        <>
                          {
                            isTodo
                              ? dayjs(toDoBy as string).diff(dayjs(), "day") < 0
                                ? type?.toLowerCase() == "comprehension"
                                  ? ""
                                  : t("exercises.overdue") // "Overdue"
                                : type?.toLowerCase() == "comprehension"
                                ? ""
                                : t("exercises.to_do_by") //" To do by:"
                              : t("exercises.grade") + ":" //"Grade:"
                          }
                          &nbsp;
                          <div className="hidden md:block">
                            <b className="font-semibold">
                              {isTodo
                                ? dayjs(toDoBy as string)
                                    .format("ddd DD/MM/YYYY")
                                    .toUpperCase()
                                    .split(".")
                                    .join(" ")
                                : (() => {
                                    const correction = getCorrection(
                                      tokenEx as string,
                                    );

                                    if (correction?.type === "Writing") {
                                      return correction?.user_score ?? "N/A";
                                    }

                                    return (
                                      correction?.ielts_score ||
                                      userScore ||
                                      votePot ||
                                      "N/A"
                                    );
                                  })()}
                            </b>
                          </div>
                          <div className="relative md:hidden">
                            <b className="font-semibold absolute top-[20px]">
                              {isTodo
                                ? dayjs(toDoBy as string)
                                    .format("ddd DD/MM/YYYY")
                                    .toUpperCase()
                                    .split(".")
                                    .join(" ")
                                : (() => {
                                    const correction = getCorrection(
                                      tokenEx as string,
                                    );

                                    if (correction?.type === "Writing") {
                                      return correction?.user_score ?? "N/A";
                                    }
                                    console.log(
                                      correction?.ielts_score,
                                      userScore,
                                      votePot,
                                    );
                                    return (
                                      correction?.ielts_score ||
                                      userScore ||
                                      votePot ||
                                      "N/A"
                                    );
                                  })()}
                            </b>
                          </div>
                        </>
                      )}
                      <div
                        className={
                          "hidden xl:block " +
                          (type?.toLowerCase() == "comprehension"
                            ? "-ml-3"
                            : "")
                        }
                      >
                        {modality === "Reading" && (
                          <span className="text-[12px] flex items-center justify-start gap-1 font-semibold text-[#2D224C]  pl-3">
                            ⏱{" "}
                            <span className="hidden md:flex">
                              {t("note.estimated_time")}
                            </span>
                            ≈ {timer} min
                          </span>
                        )}

                        {modality === "Writing" && (
                          <span className="text-[12px] flex items-center justify-start gap-1 font-semibold text-[#2D224C]  pl-3">
                            ⏱{" "}
                            <span className="hidden md:flex">
                              {t("note.estimated_time")}
                            </span>
                            ≈ {timer} min
                          </span>
                        )}
                        {modality === "Listening" && (
                          <span className="text-[12px] flex items-center justify-start gap-1 font-semibold text-[#2D224C]  pl-3">
                            ⏱️{" "}
                            <span className="hidden md:flex">
                              {t("note.estimated_time")}
                            </span>
                            ≈ {timer} min
                          </span>
                        )}
                        {modality === "Grammar" && (
                          <span className="text-[12px] flex items-center justify-start gap-1 font-semibold text-[#2D224C]  pl-3">
                            ⏱{" "}
                            <span className="hidden md:flex">
                              {t("note.estimated_time")}
                            </span>
                            ≈ {timer} min
                          </span>
                        )}
                      </div>
                      <div
                        className={
                          "xl:hidden " +
                          (type?.toLowerCase() == "comprehension"
                            ? "-ml-3"
                            : "")
                        }
                      >
                        {modality === "Reading" && (
                          <span className="text-[12px] flex items-center justify-start gap-1 font-semibold text-[#2D224C]  pl-3">
                            ⏱{" "}
                            <span className="hidden md:flex">
                              {t("note.estimated_time")}
                            </span>
                            ≈ {timer} min
                          </span>
                        )}
                        {modality === "Writing" && (
                          <span className="text-[12px] flex items-center justify-start gap-1 font-semibold text-[#2D224C]  pl-3">
                            ⏱{" "}
                            <span className="hidden md:flex">
                              {t("note.estimated_time")}
                            </span>
                            ≈ {timer} min
                          </span>
                        )}
                        {modality === "Listening" && (
                          <span className="text-[12px] flex items-center justify-start gap-1 font-semibold text-[#2D224C]  pl-3">
                            ⏱️{" "}
                            <span className="hidden md:flex">
                              {t("note.estimated_time")}
                            </span>
                            ≈ {timer} min
                          </span>
                        )}
                        {modality === "Grammar" && (
                          <span className="text-[12px] flex items-center justify-start gap-1 font-semibold text-[#2D224C]  pl-3">
                            ⏱{" "}
                            <span className="hidden md:flex">
                              {t("note.estimated_time")}
                            </span>
                            ≈ {timer} min
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* {mobile display} */}
                  <div
                    className={cn(
                      "text-xs flex flex-wrap w-full text-primary md:hidden  relative items-center justify-between",
                      {
                        "text-[#E31837]":
                          dayjs(toDoBy as string).diff(dayjs(), "day") < 0,
                      },
                    )}
                  >
                    <div className="flex top-[17px] absolute">
                      {type?.toLowerCase() == "comprehension" ? (
                        <></>
                      ) : (
                        <>
                          <div className="text-[#2D224C]">
                            {
                              isTodo
                                ? dayjs(toDoBy as string).diff(dayjs(), "day") <
                                  0
                                  ? t("exercises.overdue") // "Overdue"
                                  : t("exercises.to_do_by") //" To do by:"
                                : t("exercises.grade") + ":" //"Grade:"
                            }
                            &nbsp;
                          </div>
                          <b className="font-semibold text-[#2D224C]">
                            {isTodo
                              ? dayjs(toDoBy as string)
                                  .format("ddd DD/MM/YYYY")
                                  .toUpperCase()
                                  .split(".")
                                  .join(" ")
                              : (() => {
                                  const correction = getCorrection(
                                    tokenEx as string,
                                  );

                                  if (correction?.type === "Writing") {
                                    return correction?.user_score ?? "N/A";
                                  }

                                  return (
                                    correction?.ielts_score ||
                                    userScore ||
                                    votePot ||
                                    "N/A"
                                  );
                                })()}
                          </b>
                        </>
                      )}
                      {modality === "Reading" && (
                        <span className="text-[12px] flex items-center justify-start gap-1 font-semibold text-[#2D224C]  pl-3">
                          ⏱{" "}
                          <span className="hidden md:flex">
                            {t("note.estimated_time")}
                          </span>
                          ≈ {timer} min
                        </span>
                      )}
                      {modality === "Writing" && (
                        <span
                          className={
                            "text-[12px] flex items-center justify-start gap-1 font-semibold text-[#2D224C] " +
                            (type?.toLowerCase() == "comprehension"
                              ? ""
                              : "pl-3")
                          }
                        >
                          ⏱{" "}
                          <span className="hidden md:flex">
                            {t("note.estimated_time")}
                          </span>
                          ≈ {timer} min
                        </span>
                      )}
                      {modality === "Listening" && (
                        <span
                          className={
                            "text-[12px] flex items-center justify-start gap-1 font-semibold text-[#2D224C] " +
                            (type?.toLowerCase() == "comprehension"
                              ? ""
                              : "pl-3")
                          }
                        >
                          ⏱️{" "}
                          <span className="hidden md:flex">
                            {t("note.estimated_time")}
                          </span>
                          ≈ {timer} min
                        </span>
                      )}
                      {modality === "Grammar" && (
                        <span
                          className={
                            "text-[12px] flex items-center justify-start gap-1 font-semibold text-[#2D224C] " +
                            (type?.toLowerCase() == "comprehension"
                              ? ""
                              : "pl-3")
                          }
                        >
                          ⏱{" "}
                          <span className="hidden md:flex">
                            {t("note.estimated_time")}
                          </span>
                          ≈ {timer} min
                        </span>
                      )}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {/* {status === "done" &&
            props.exercise?.type?.toLowerCase() !== "comprehension" &&   (
              <div className="relative flex flex-row items-center w-full gap-2">
                <p className="hidden text-xs text-primary md:block">
                  {t("exercises.grade")}:&nbsp;
                  <b className="font-semibold">
                    {(() => {
                      const correction = getCorrection(tokenEx as string);
                      const userScoreC = correction?.user_score;

                      const qN = Object.keys(correction?.questions ?? {})
                        ?.length;

                      const usN = Object.keys(correction?.user_submit ?? {})
                        ?.length;

                      if (correction?.type === "Writing") {
                        if (userScoreC) {
                          return userScoreC;
                        } else {
                          const o = qN - usN;
                          return `${o < 0 ? 0 : o}/${qN}`;
                        }
                      }

                      return (
                        correction?.ielts_score ||
                        userScore ||
                        votePot ||
                        `${qN - usN}/${qN}` ||
                        "N/A"
                      );
                    })()}
                  </b>
                </p>
                <p className="text-xs text-primary md:hidden absolute top-[20px]">
                  {t("exercises.grade")}:&nbsp;
                  <b className="font-semibold">
                    {(() => {
                      const correction = getCorrection(tokenEx as string);

                      if (correction?.type === "Writing") {
                        return correction?.user_score ?? "N/A";
                      }

                      return (
                        correction?.ielts_score || userScore || votePot || "N/A"
                      );
                    })()}
                  </b>
                </p>
              </div>
            )} */}
          {status === "done" &&
            props.exercise?.type?.toLowerCase() !== "comprehension" && (
              <div className="relative flex flex-row items-center w-full gap-2">
                {(() => {
                  const correction = getCorrection(tokenEx as string);

                  if (
                    props.exercise?.type?.toLowerCase() === "writing" &&
                    correction?.corrected === null
                  ) {
                    return null; // Do not display any score
                  }

                  // console.log("Exercise done types: ",correction?.type)

                  return (
                    <>
                      {/* Desktop View */}
                      <p className="hidden text-xs text-primary md:block">
                        {t("exercises.grade")}:&nbsp;
                        <b className="font-semibold">
                          {(() => {
                            const questionCount = Object.keys(
                              correction?.questions ?? {},
                            ).length;
                            const userSubmitCount = Object.keys(
                              correction?.user_submit ?? {},
                            ).length;
                            const exerciseType =
                              correction?.type?.toLowerCase?.();

                            const fallbackScore = `${
                              questionCount - userSubmitCount
                            }/${questionCount}`;

                            switch (exerciseType) {
                              case "listening":
                                return getIELTSListeningBandScore(correction?.user_score);
                              case "reading":
                                return getIELTSReadingBandScore(correction?.user_score);
                              case "grammar":
                                return correction?.user_score != null
                                  ? correction.user_score
                                  : `0/${questionCount ?? "?"}`;

                              case "comprehension":
                                return correction?.user_score ?? fallbackScore;

                              case "writing":
                                return correction?.user_score ?? fallbackScore;
                              case "speaking":
                                return correction?.user_score;

                              default:
                                return (
                                  correction?.ielts_score ??
                                  userScore ??
                                  votePot ??
                                  fallbackScore ??
                                  "N/A"
                                );
                            }
                          })()}
                        </b>
                      </p>

                      {/* Mobile View */}
                      <p className="text-xs text-primary md:hidden absolute top-[20px]">
                        {t("exercises.grade")}:&nbsp;
                        <b className="font-semibold">
                          {(() => {
                            const exerciseType =
                              correction?.type?.toLowerCase?.();

                            if (exerciseType === "writing") {
                              return correction?.user_score ?? "N/A";
                            }

                            return (
                              correction?.ielts_score ??
                              userScore ??
                              votePot ??
                              "N/A"
                            );
                          })()}
                        </b>
                      </p>
                    </>
                  );
                })()}
              </div>
            )}
          <div className="relative">
            <h2 className="text-base text-secondary hidden md:block !font-semibold line-clamp-1">
              {isTodo ? name : testName || name}
            </h2>
            <h2 className="text-base md:hidden text-secondary absolute !font-semibold line-clamp-1 -top-2">
              {isTodo ? name : testName || name}
            </h2>
          </div>
        </div>

        <div className="flex flex-row items-center justify-end gap-2 ml-auto">
          {isWriting &&
            !isTodo &&
            !corrected &&
            canBookWritings &&
            !isBook98WL && (
              <>
                <Button
                  data-track-props={mixpanelPropsData}
                  data-track-eventtype={
                    MIXPANEL_EVENT_TYPES.EXERCISE_BOOK_LIVE_CORRECTION
                  }
                  variant="contained"
                  onClick={() => mutateCheckBookLiveCorrection()}
                  className="text-brand-secondary px-3 w-[300px] xl:flex hidden [--bg:--primary100] [--border:---primary100] border-[--primary100] leading-4"
                >
                  {t("exercises.book_live_correction")}
                </Button>

                <span
                  role="button"
                  data-track-props={mixpanelPropsData}
                  data-track-eventtype={
                    MIXPANEL_EVENT_TYPES.EXERCISE_BOOK_LIVE_CORRECTION
                  }
                  tabIndex={0}
                  className="text-brand-secondary cursor-pointer px-4 py-1 max-w-fit xl:hidden bg-[--primary100] rounded-md"
                  onKeyUp={() => navigate("correction/book-live")}
                  onClick={() => navigate("correction/book-live")}
                >
                  {t("btns.book_live_correction_mobile")}
                </span>
              </>
            )}

          {props.exercise.isPot && !isTodo && (
            <Button
              variant="contained"
              onClick={() => {
                setGlobalState("isPot", props.exercise.isPot);
                navigate(
                  `${routes.exercises}/pot/${exId}?isDone=true&isPot=true`,
                );
              }}
              className="until-lg:max-w-[20px] until-lg:min-w-[20px] until-lg:bg-transparent until-lg:text-[--primary] text-white until-lg:border-none until-lg:ml-auto"
            >
              <p className="hidden xl:block">{t("exercises.view")}</p>

              <EyeIcon className="h-auto w-5 fill-[--primary] md:fill-current xl:hidden" />
            </Button>
          )}

          {!isTodo && !isSpeaking && !props.exercise.isPot && (
            <>
              <Button
                data-track-props={mixpanelPropsData}
                data-track-eventtype={
                  MIXPANEL_EVENT_TYPES.EXERCISE_DO_IT_BUTTON
                }
                size="sm"
                onClick={() => {
                  if (!testName?.toLowerCase().includes("mock test")) {
                    handleDoItClick();
                  }
                }}
                className="until-lg:max-w-[20px] until-lg:min-w-[20px] until-lg:bg-transparent until-lg:text-[--primary] text-white until-lg:border-none until-lg:ml-auto"
              >
                <p className="hidden xl:block">{t("exercises.view")}</p>

                <EyeIcon className="h-auto w-5 fill-[--primary] md:fill-current xl:hidden" />
              </Button>
            </>
          )}

          {isTodo && (
            <Button
              data-track-props={mixpanelPropsData}
              data-track-eventtype={MIXPANEL_EVENT_TYPES.EXERCISE_DO_IT_BUTTON}
              size="sm"
              onClick={() => {
                if (!testName?.toLowerCase().includes("mock test")) {
                  handleDoItClick();
                }
              }}
              className="until-lg:max-w-[20px] until-lg:min-w-[20px] until-lg:bg-transparent until-lg:text-[--primary] until-lg:border-none ml-auto"
            >
              <p className="hidden xl:block">{t("exercises.do_it")}</p>

              <ArrowRight className="w-4 h-auto stroke-brand-secondary md:stroke-current xl:hidden " />
            </Button>
          )}
        </div>
      </div>
    </CardItem>
  );
};

export default ExerciseItem;
